/**
 * MacroStepWaiters — keyed waiter map + reply demux for the macro-step IPC
 * channel (production-readiness Phase 5, R13 host half / macro G2+G3).
 *
 * Extracted from `PlayjectorService` so the service file stays within the
 * size budget (phase-doc risk note): the service keeps the public seam
 * (`requestMacroStep` / `notifyMacroStepResult`) and delegates correlation
 * here.
 *
 * Keying mirrors `AndroidInstanceMessenger`'s proven pattern: a waiter is
 * keyed by `${streamId}:${gameInstanceIndex}`; the aggregate path (no
 * instance target) uses the `'legacy'` key. Single-flight is enforced PER KEY
 * — the legacy key therefore keeps the historical single-flight rejection,
 * while distinct instance targets run concurrently.
 *
 * Reply demux: a reply's echoed `streamId`/`gameInstanceIndex` selects the
 * waiter; a reply without a usable identity resolves the `'legacy'` waiter
 * (pre-Phase-5 behavior preserved).
 *
 * Native slot latch (D3/D8): pre-Phase-5, Playjector's C++ side tracked only
 * ONE in-flight macro step process-wide (`ProcessDirectory::onMacroStep`
 * stored into a single `m_ProcessInfo.macroVariants`). For the legacy
 * (untargeted) key that single-process assumption still holds, so when its
 * JS-side waiter times out or is abandoned, the legacy key stays marked
 * "outstanding" — blocking a fresh `begin('legacy')` — until either the late
 * C++ reply arrives (`notify()`, D8 ignore-and-free) or
 * `MACRO_STEP_NATIVE_SLOT_DRAIN_MS` elapses, whichever comes first. Without
 * this, a fresh request could race a late reply from the previous request
 * onto a brand-new waiter (D3).
 *
 * Per-instance keyed waiters (production-readiness Phase 5) do NOT hold this
 * latch: each instance's capture is independently addressed by
 * `{streamId, gameInstanceIndex}` on both the request and its echoed reply,
 * so a timed-out instance key is simply freed — a later reply for that same
 * key, if it ever arrives, is dropped as "no waiter" (still safe) rather than
 * needing a native-process-wide latch.
 *
 * Known exposure (accepted — Phase 8 review nit N6): correlation is
 * key-only. Requests carry no request nonce, so a timed-out waiter's late
 * reply can resolve a NEW waiter that reused the same key (stale-reply
 * aliasing) — the same exposure the pre-diff single waiter had. The C++
 * echo schema carries no nonce to match against, so this stays accepted
 * for now.
 */

import type {
  MacroStepCaptureResult,
  MacroStepReply,
  MacroStepTarget,
} from './interfaces/IPlayjectorService';

/**
 * How long to wait for a `processMacroStepReplyMSG` from Playjector before
 * rejecting the in-flight `requestMacroStep` promise. Sized generously — the
 * C++ side serializes the screenshot capture, hash compute, and JSON reply,
 * but typical replies arrive in well under 2s.
 */
export const MACRO_STEP_REPLY_TIMEOUT_MS = 10_000;

/** After a timeout/abandon, keep the native slot latched until a late reply or this drain. */
export const MACRO_STEP_NATIVE_SLOT_DRAIN_MS = 30_000;

/**
 * The exact reply error the C++ instance path returns when the target
 * instance's monitor has not finished DisplayGrabber convergence yet
 * (`monitorIndex < 0`; macro G9). Compared with `===` — substring matches on
 * other errors must NOT trigger the retry.
 */
export const MONITOR_NOT_READY_REPLY_ERROR = 'monitor not ready';

/**
 * Bounded retry backoff for `monitor not ready` (macro G9): after the initial
 * attempt fails with that exact error, resend up to 3 times, waiting
 * 500ms / 1s / 2s between sends (4 sends worst case, ~3.5s of backoff).
 * Exhaustion surfaces the failure honestly to the caller.
 */
export const MONITOR_NOT_READY_RETRY_DELAYS_MS: readonly number[] = [500, 1_000, 2_000];

/** Waiter-map key for the aggregate (untargeted) macro-step path. */
const LEGACY_KEY = 'legacy';

/**
 * Rejection used when a reply carries exactly `monitor not ready` — a
 * distinct class so `requestMacroStep`'s retry loop can match the transient
 * condition precisely (`instanceof`) instead of substring-sniffing messages.
 * The message keeps the historical `Playjector macro step error: …` shape so
 * the final surfaced failure reads like any other reply error.
 */
export class MacroStepMonitorNotReadyError extends Error {
  constructor() {
    super(`Playjector macro step error: ${MONITOR_NOT_READY_REPLY_ERROR}`);
    this.name = 'MacroStepMonitorNotReadyError';
  }
}

interface MacroStepWaiter {
  resolve(value: Record<string, MacroStepCaptureResult>): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * Per-key bookkeeping: `waiter` is the live promise settlement pair (absent
 * once timed out/settled); `outstanding` tracks the native (C++-side) slot
 * independently of `waiter` so the D3/D8 latch survives past the JS timeout;
 * `drainTimer` bounds how long the latch survives without a reply.
 */
interface MacroStepKeyState {
  waiter?: MacroStepWaiter;
  outstanding: boolean;
  drainTimer: ReturnType<typeof setTimeout> | null;
}

export class MacroStepWaiters {
  private readonly keys = new Map<string, MacroStepKeyState>();

  /** Waiter key for an outbound request target (absent target → legacy). */
  public static keyForTarget(target?: MacroStepTarget): string {
    return target ? `${target.streamId}:${target.gameInstanceIndex}` : LEGACY_KEY;
  }

  /**
   * Waiter key for an inbound reply's echoed identity. Identity is only
   * honored when BOTH fields are usable (non-empty streamId, non-negative
   * index) — a partial/absent echo falls back to the legacy waiter, matching
   * the pre-Phase-5 single-waiter behavior.
   */
  public static keyForReply(reply: { streamId?: string; gameInstanceIndex?: number }): string {
    const hasIdentity =
      typeof reply.streamId === 'string'
      && reply.streamId.length > 0
      && typeof reply.gameInstanceIndex === 'number'
      && reply.gameInstanceIndex >= 0;
    return hasIdentity ? `${reply.streamId}:${reply.gameInstanceIndex}` : LEGACY_KEY;
  }

  private getOrCreateState(key: string): MacroStepKeyState {
    let state = this.keys.get(key);
    if (!state) {
      state = { outstanding: false, drainTimer: null };
      this.keys.set(key, state);
    }
    return state;
  }

  /** Drop a key's bookkeeping once neither a live waiter nor the native latch remain. */
  private pruneIfIdle(key: string, state: MacroStepKeyState): void {
    if (!state.waiter && !state.outstanding && !state.drainTimer) {
      this.keys.delete(key);
    }
  }

  private armNativeSlotDrain(key: string, state: MacroStepKeyState): void {
    if (state.drainTimer) return;
    state.drainTimer = setTimeout(() => {
      state.drainTimer = null;
      state.outstanding = false;
      this.pruneIfIdle(key, state);
    }, MACRO_STEP_NATIVE_SLOT_DRAIN_MS);
  }

  private clearNativeSlotDrain(state: MacroStepKeyState): void {
    if (!state.drainTimer) return;
    clearTimeout(state.drainTimer);
    state.drainTimer = null;
  }

  /**
   * Register a waiter for `key` and return its promise. Throws synchronously
   * when a request for the same key is already in flight OR the native slot
   * is still latched from a prior timeout/abandon (per-key single-flight).
   * The waiter rejects after `MACRO_STEP_REPLY_TIMEOUT_MS`; the native latch
   * survives that rejection until `notify()` or the drain window elapses.
   */
  public begin(key: string): Promise<Record<string, MacroStepCaptureResult>> {
    const state = this.getOrCreateState(key);
    if (state.waiter || state.outstanding) {
      throw new Error(
        key === LEGACY_KEY
          ? 'A macro step request is already in flight'
          : `A macro step request is already in flight for instance ${key}`,
      );
    }

    return new Promise<Record<string, MacroStepCaptureResult>>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        state.waiter = undefined;
        console.error(
          `[StatefulMacroSystemStep] ⏱️ processMacroStep reply timed out after ${MACRO_STEP_REPLY_TIMEOUT_MS}ms — Playjector did not reply`,
          { timeoutMs: MACRO_STEP_REPLY_TIMEOUT_MS, waiterKey: key },
        );
        if (key === LEGACY_KEY) {
          // Keep the native slot outstanding until the late C++ reply (D8) or
          // the drain window elapses, so a fresh request cannot race it (D3).
          this.armNativeSlotDrain(key, state);
        } else {
          state.outstanding = false;
          this.pruneIfIdle(key, state);
        }
        reject(new Error(`Macro step reply timed out after ${MACRO_STEP_REPLY_TIMEOUT_MS}ms`));
      }, MACRO_STEP_REPLY_TIMEOUT_MS);

      state.waiter = { resolve, reject, timeoutId };
      state.outstanding = true;
    });
  }

  /**
   * Demux a `processMacroStepReplyMSG` payload to its waiter by echoed
   * identity (absent identity → legacy) and settle it. A reply with no
   * matching waiter is logged and dropped; it still frees the native slot
   * latch for that key (D8) so subsequent requests are unblocked.
   */
  public notify(
    reply: MacroStepReply & { error?: string; streamId?: string; gameInstanceIndex?: number },
  ): void {
    const key = MacroStepWaiters.keyForReply(reply);
    const state = this.keys.get(key);
    if (!state) {
      console.warn(
        '[StatefulMacroSystemStep] ⚠️  Received Playjector macro step reply with no waiter — dropping',
        { waiterKey: key },
      );
      return;
    }

    this.clearNativeSlotDrain(state);
    state.outstanding = false;

    const waiter = state.waiter;
    if (!waiter) {
      console.warn(
        '[StatefulMacroSystemStep] ⚠️  Received late Playjector macro step reply after timeout/abandon — dropping',
        { waiterKey: key },
      );
      this.pruneIfIdle(key, state);
      return;
    }
    state.waiter = undefined;
    clearTimeout(waiter.timeoutId);
    this.pruneIfIdle(key, state);

    if (reply.error) {
      console.error('[StatefulMacroSystemStep] ❌ Playjector macro step reply error:', reply.error, {
        waiterKey: key,
      });
      waiter.reject(
        reply.error === MONITOR_NOT_READY_REPLY_ERROR
          ? new MacroStepMonitorNotReadyError()
          : new Error(`Playjector macro step error: ${reply.error}`),
      );
      return;
    }

    if (!reply.variantResults || typeof reply.variantResults !== 'object') {
      console.error('[StatefulMacroSystemStep] ❌ Playjector macro step reply missing variantResults — rejecting', {
        waiterKey: key,
        replyShape: Object.keys(reply ?? {}),
      });
      waiter.reject(new Error('Macro step reply missing variantResults'));
      return;
    }

    console.log('[StatefulMacroSystemStep] 📨 Received Playjector screenshot reply', {
      waiterKey: key,
      variantCount: Object.keys(reply.variantResults).length,
      captureIds: Object.keys(reply.variantResults),
    });
    waiter.resolve(reply.variantResults);
  }

  /**
   * Reject and clear every currently in-flight waiter (walk teardown /
   * campaign abandonment — callers don't know which instance keys, if any,
   * still have an outstanding request, so this abandons all of them). Native
   * slot latches survive this call (armed to drain) so a late C++ reply
   * cannot alias a freshly-started waiter for the same key.
   */
  public abandonAll(): void {
    for (const [key, state] of this.keys) {
      const waiter = state.waiter;
      if (waiter) {
        state.waiter = undefined;
        clearTimeout(waiter.timeoutId);
        waiter.reject(new Error('Macro step abandoned'));
      }
      if (state.outstanding && key === LEGACY_KEY) {
        this.armNativeSlotDrain(key, state);
      } else {
        state.outstanding = false;
        this.pruneIfIdle(key, state);
      }
    }
  }
}
